import { ReactNode, useCallback, useEffect, useState } from 'react';

import { getLocalStorage } from '@libs/storage';

import { OPPOSITE_THEMES, Theme, ThemeContext } from './ThemeContext';

const LOCAL_STORAGE_KEY = 'theme';
const LOCAL_STORAGE = getLocalStorage();
const URL_QUERY = 'theme';

export function ThemeProvider({
  children,
  defaultTheme,
}: {
  children: ReactNode;
  defaultTheme?: 'dark' | 'light';
}) {
  const [theme, setTheme] = useState(() => {
    return getFromQueryString() ?? getFromLocalStorage() ?? defaultTheme ?? 'light';
  });

  const value = {
    theme,
    selectTheme: useCallback((t: Theme) => {
      LOCAL_STORAGE.setItem(LOCAL_STORAGE_KEY, t);
      setTheme(t);
    }, []),
    toggleTheme: useCallback(() => {
      const oppositeTheme = OPPOSITE_THEMES[theme];
      LOCAL_STORAGE.setItem(LOCAL_STORAGE_KEY, oppositeTheme);
      setTheme(oppositeTheme);
    }, [theme]),
  };

  useEffect(() => {
    const isDark = document.documentElement.classList.contains('theme-dark');

    if ((theme === 'dark' && isDark) || (theme === 'light' && !isDark)) return;

    // выключить анимации цветов при переключении темы
    let switching = true;
    document.documentElement.classList.add('switching-theme');

    const raf = requestAnimationFrame(() => {
      switching = false;
      document.documentElement.classList.remove('switching-theme');
    });

    if (theme === 'dark') {
      document.documentElement.classList.add('theme-dark');
    }

    if (theme === 'light') {
      document.documentElement.classList.remove('theme-dark');
    }

    return () => {
      if (switching) {
        document.documentElement.classList.remove('switching-theme');
        cancelAnimationFrame(raf);
      }
    };
  }, [theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

function getFromQueryString(): Theme | undefined {
  const search = new URLSearchParams(location.search);
  const query = search.get(URL_QUERY);
  if (query === 'dark' || query === 'light') {
    return query;
  }
}

function getFromLocalStorage(): Theme | undefined {
  return LOCAL_STORAGE.getItem(LOCAL_STORAGE_KEY) as Theme | undefined;
}
